'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';

// ─── MRG WIDGET — mirrors full MRG page structure ─────────────────────────────
// Panels: Names/Attributes, Ayat Mosaic, Uboor, Resources, Context, Data
// At large sizes: same format/structure as full MRG page
// At smaller sizes: content shifts to adjust for sizing constraints

interface MRGWidgetProps {
  size: 'sm' | 'md' | 'lg' | 'xl';
  color: string;
  city?: string;
}

// ── Resource categories with Names of Allah ──────────────────────────────────
const RESOURCE_CATEGORIES_MRG = [
  { key: 'Food', nameOfAllah: 'AR-RAZZAQ', arabicName: 'الرَّزَّاق', meaning: 'The Ever-Provider', icon: '🌿', ayat: { ref: '11:6', arabic: 'وَمَا مِن دَابَّةٍ فِي الْأَرْضِ إِلَّا عَلَى اللَّهِ رِزْقُهَا', text: 'There is no creature on earth whose provision is not guaranteed by Allah.' }, context: 'Food deserts are not natural phenomena — they are the accumulated weight of redlining, disinvestment, and structural neglect. The Rizq of Allah exists in the earth; the Amanah is to trace where the barriers are and bridge to Islah.' },
  { key: 'Health', nameOfAllah: 'ASH-SHAFI', arabicName: 'الشَّافِي', meaning: 'The Healer', icon: '🤲', ayat: { ref: '26:80', arabic: 'وَإِذَا مَرِضْتُ فَهُوَ يَشْفِينِ', text: 'And when I am ill, it is He who cures me.' }, context: 'Healthcare deserts follow the same boundaries as food deserts and redlining maps. The Amanah of Ash-Shafi is to bridge the sick to healing, to advocate for health equity.' },
  { key: 'Housing', nameOfAllah: 'AL-WALI', arabicName: 'الْوَلِيّ', meaning: 'The Protecting Friend', icon: '🏘', ayat: { ref: '16:80', arabic: 'وَاللَّهُ جَعَلَ لَكُم مِّن بُيُوتِكُمْ سَكَنًا', text: 'Allah has given you homes as places of rest.' }, context: 'Displacement is violence. The Amanah of Al-Wali is to protect communities from displacement, to preserve affordable housing.' },
  { key: 'Education', nameOfAllah: 'AL-ALIM', arabicName: 'الْعَلِيم', meaning: 'The All-Knowing', icon: '📖', ayat: { ref: '96:1', arabic: 'اقْرَأْ بِاسْمِ رَبِّكَ الَّذِي خَلَقَ', text: 'Read in the name of your Lord who created.' }, context: 'The first command of revelation was to read. Educational disinvestment is a direct transgression against this command.' },
  { key: 'Reentry', nameOfAllah: 'AL-AFUWW', arabicName: 'الْعَفُوّ', meaning: 'The Pardoner', icon: '🌱', ayat: { ref: '39:53', arabic: 'لَا تَقْنَطُوا مِن رَّحْمَةِ اللَّهِ', text: 'Do not despair of the mercy of Allah.' }, context: 'The returning citizen carries the weight of a system designed to recycle, not rehabilitate. The Amanah of Al-Afuww is to receive them with mercy.' },
  { key: 'Justice', nameOfAllah: 'AL-MUQSIT', arabicName: 'الْمُقْسِط', meaning: 'The Equitable', icon: '⚖', ayat: { ref: '4:135', arabic: 'كُونُوا قَوَّامِينَ بِالْقِسْطِ', text: 'Be persistently standing firm in justice.' }, context: 'The redlining map and the food desert map are the same map. The Amanah of Al-Muqsit is to name the injustice and bridge caretakers to legal aid.' },
  { key: 'Community', nameOfAllah: 'AL-WADUD', arabicName: 'الْوَدُود', meaning: 'The Loving', icon: '🕌', ayat: { ref: '49:10', arabic: 'إِنَّمَا الْمُؤْمِنُونَ إِخْوَةٌ', text: 'The believers are but brothers.' }, context: 'Community is the infrastructure of Islah. Masajid, community centers, and cultural organizations are the connective tissue that holds neighborhoods together.' },
  { key: 'Income', nameOfAllah: 'AL-MUGHNI', arabicName: 'الْمُغْنِي', meaning: 'The Enricher', icon: '💼', ayat: { ref: '4:32', arabic: 'وَاسْأَلُوا اللَّهَ مِن فَضْلِهِ', text: 'Ask Allah for His bounty.' }, context: 'Income disparities are the direct legacy of wage theft, redlining, and structural exclusion from wealth-building.' },
];

// ── City data ─────────────────────────────────────────────────────────────────
const CITIES_MRG = [
  {
    id: 'chicago', name: 'Chicago', arabicName: 'شيكاغو',
    keyWard: 'Englewood', wardSeverity: 'CRITICAL' as const,
    wardStats: [{ label: 'Food Desert', value: '85%' }, { label: 'Vacancy', value: '55%' }, { label: 'Poverty', value: '45%' }],
    masajid: ['IMAN Chicago HQ', 'Masjid Al-Fatir', 'Masjid Al-Taqwa', 'Mosque Maryam', 'Masjid Al-Faatir'],
    wards: [
      { name: 'Englewood', severity: 'CRITICAL' as const, stats: [{ label: 'Food Desert', value: '85%' }, { label: 'Vacancy', value: '55%' }, { label: 'Poverty', value: '45%' }] },
      { name: 'Austin', severity: 'CRITICAL' as const, stats: [{ label: 'Healthcare Gap', value: '42%' }, { label: 'Unemployment', value: '38%' }, { label: 'Poverty', value: '40%' }] },
      { name: 'South Shore', severity: 'HIGH' as const, stats: [{ label: 'Food Desert', value: '60%' }, { label: 'Vacancy', value: '30%' }, { label: 'Poverty', value: '35%' }] },
      { name: 'Roseland', severity: 'CRITICAL' as const, stats: [{ label: 'Food Desert', value: '78%' }, { label: 'Vacancy', value: '48%' }, { label: 'Poverty', value: '42%' }] },
      { name: 'Woodlawn', severity: 'HIGH' as const, stats: [{ label: 'Displacement', value: '55%' }, { label: 'Poverty', value: '38%' }, { label: 'Vacancy', value: '25%' }] },
      { name: 'North Lawndale', severity: 'CRITICAL' as const, stats: [{ label: 'Food Desert', value: '80%' }, { label: 'Poverty', value: '44%' }, { label: 'Vacancy', value: '50%' }] },
      { name: 'Pilsen', severity: 'MODERATE' as const, stats: [{ label: 'Displacement', value: '35%' }, { label: 'Poverty', value: '28%' }, { label: 'Vacancy', value: '15%' }] },
      { name: 'Bronzeville', severity: 'HIGH' as const, stats: [{ label: 'Poverty', value: '32%' }, { label: 'Vacancy', value: '22%' }, { label: 'Food Desert', value: '45%' }] },
    ],
    topResources: [
      { name: 'IMAN — Inner-City Muslim Action Network', category: 'Community', nameOfAllah: 'AL-WADUD' },
      { name: 'IMAN Green ReEntry', category: 'Reentry', nameOfAllah: 'AL-AFUWW' },
      { name: 'Greater Chicago Food Depository', category: 'Food', nameOfAllah: 'AR-RAZZAQ' },
      { name: 'Heartland Alliance', category: 'Housing', nameOfAllah: 'AL-WALI' },
      { name: 'Chicago Urban League', category: 'Income', nameOfAllah: 'AL-MUGHNI' },
    ],
    ayat: { arabic: 'وَمَا مِن دَابَّةٍ فِي الْأَرْضِ إِلَّا عَلَى اللَّهِ رِزْقُهَا', ref: '11:6', nameOfAllah: 'AR-RAZZAQ', arabicName: 'الرَّزَّاق', meaning: 'The Ever-Provider' },
    disparities: [
      { issue: 'Food Insecurity', value: '85%', ward: 'Englewood', nameOfAllah: 'AR-RAZZAQ', arabicName: 'الرَّزَّاق' },
      { issue: 'Healthcare Gap', value: '42%', ward: 'Austin', nameOfAllah: 'ASH-SHAFI', arabicName: 'الشَّافِي' },
      { issue: 'Youth Unemployment', value: '38%', ward: 'South Side', nameOfAllah: 'AL-MUGHNI', arabicName: 'الْمُغْنِي' },
    ],
    context: "IMAN's birthplace. Englewood, South Shore, Austin — the full weight of the Great Migration, redlining, and Muslim community development. The South Side carries the Amanah of the entire city.",
    openLetterQuote: 'IMAN was born in Englewood — from the community, for the community. The Amanah is to be the roots that hold the soil.',
    wardCount: 8,
    wardSeverities: ['CRITICAL', 'CRITICAL', 'HIGH', 'CRITICAL', 'HIGH', 'MODERATE', 'HIGH', 'CRITICAL'] as const,
  },
  {
    id: 'atlanta', name: 'Atlanta', arabicName: 'أطلانتا',
    keyWard: 'English Ave / Vine City', wardSeverity: 'CRITICAL' as const,
    wardStats: [{ label: 'Food Desert', value: '90%' }, { label: 'Vacancy', value: '52%' }, { label: 'Poverty', value: '49%' }],
    masajid: ['Community Masjid of Atlanta', 'Atlanta Masjid of Al-Islam', 'IMAN Atlanta', 'Masjid Al-Muminun'],
    wards: [
      { name: 'English Ave', severity: 'CRITICAL' as const, stats: [{ label: 'Food Desert', value: '90%' }, { label: 'Vacancy', value: '52%' }, { label: 'Poverty', value: '49%' }] },
      { name: 'Vine City', severity: 'CRITICAL' as const, stats: [{ label: 'Food Desert', value: '85%' }, { label: 'Poverty', value: '46%' }, { label: 'Vacancy', value: '48%' }] },
      { name: 'West End', severity: 'HIGH' as const, stats: [{ label: 'Displacement', value: '72%' }, { label: 'Poverty', value: '35%' }, { label: 'Vacancy', value: '20%' }] },
      { name: 'East Lake', severity: 'MODERATE' as const, stats: [{ label: 'Poverty', value: '22%' }, { label: 'Vacancy', value: '12%' }, { label: 'Food Desert', value: '30%' }] },
      { name: 'Mechanicsville', severity: 'CRITICAL' as const, stats: [{ label: 'Food Desert', value: '88%' }, { label: 'Poverty', value: '50%' }, { label: 'Vacancy', value: '55%' }] },
      { name: 'Pittsburgh', severity: 'HIGH' as const, stats: [{ label: 'Poverty', value: '40%' }, { label: 'Vacancy', value: '35%' }, { label: 'Food Desert', value: '65%' }] },
      { name: 'Summerhill', severity: 'MODERATE' as const, stats: [{ label: 'Displacement', value: '45%' }, { label: 'Poverty', value: '30%' }, { label: 'Vacancy', value: '18%' }] },
      { name: 'Peoplestown', severity: 'HIGH' as const, stats: [{ label: 'Poverty', value: '38%' }, { label: 'Vacancy', value: '28%' }, { label: 'Food Desert', value: '60%' }] },
    ],
    topResources: [
      { name: 'Community Masjid of Atlanta', category: 'Community', nameOfAllah: 'AL-WADUD' },
      { name: 'Westside Future Fund', category: 'Housing', nameOfAllah: 'AL-WALI' },
      { name: 'Atlanta Community Food Bank', category: 'Food', nameOfAllah: 'AR-RAZZAQ' },
    ],
    ayat: { arabic: 'كُونُوا قَوَّامِينَ بِالْقِسْطِ شُهَدَاءَ لِلَّهِ', ref: '4:135', nameOfAllah: 'AL-MUQSIT', arabicName: 'الْمُقْسِط', meaning: 'The Equitable' },
    disparities: [
      { issue: 'Food Desert', value: '90%', ward: 'English Ave', nameOfAllah: 'AR-RAZZAQ', arabicName: 'الرَّزَّاق' },
      { issue: 'Displacement', value: '72%', ward: 'West End', nameOfAllah: 'AL-WALI', arabicName: 'الْوَلِيّ' },
    ],
    context: 'Heart of the Black Belt South. English Avenue, West End, East Lake — redlining maps that still govern food deserts today.',
    openLetterQuote: 'We were commanded to come down for a time as Caretakers — to see what is broken and to bridge it.',
    wardCount: 8,
    wardSeverities: ['CRITICAL', 'CRITICAL', 'CRITICAL', 'CRITICAL', 'HIGH', 'HIGH', 'MODERATE', 'HIGH'] as const,
  },
  {
    id: 'detroit', name: 'Detroit', arabicName: 'ديترويت',
    keyWard: 'Hamtramck', wardSeverity: 'HIGH' as const,
    wardStats: [{ label: 'Vacancy', value: '55%' }, { label: 'Food Desert', value: '85%' }, { label: 'Poverty', value: '48%' }],
    masajid: ['American Moslem Society (est. 1938)', 'Masjid Wali Muhammad', 'Hamtramck Islamic Center'],
    wards: [
      { name: 'Hamtramck', severity: 'HIGH' as const, stats: [{ label: 'Poverty', value: '35%' }, { label: 'Food Desert', value: '60%' }, { label: 'Vacancy', value: '25%' }] },
      { name: 'Brightmoor', severity: 'CRITICAL' as const, stats: [{ label: 'Vacancy', value: '55%' }, { label: 'Food Desert', value: '85%' }, { label: 'Poverty', value: '48%' }] },
      { name: 'East Side', severity: 'HIGH' as const, stats: [{ label: 'Food Desert', value: '80%' }, { label: 'Poverty', value: '42%' }, { label: 'Vacancy', value: '40%' }] },
      { name: 'Dearborn', severity: 'MODERATE' as const, stats: [{ label: 'Poverty', value: '22%' }, { label: 'Food Desert', value: '30%' }, { label: 'Vacancy', value: '10%' }] },
      { name: 'Southwest Detroit', severity: 'HIGH' as const, stats: [{ label: 'Poverty', value: '38%' }, { label: 'Food Desert', value: '65%' }, { label: 'Vacancy', value: '30%' }] },
      { name: 'Midtown', severity: 'MODERATE' as const, stats: [{ label: 'Poverty', value: '25%' }, { label: 'Vacancy', value: '15%' }, { label: 'Food Desert', value: '35%' }] },
      { name: 'Poletown', severity: 'HIGH' as const, stats: [{ label: 'Vacancy', value: '45%' }, { label: 'Poverty', value: '40%' }, { label: 'Food Desert', value: '70%' }] },
      { name: 'North End', severity: 'CRITICAL' as const, stats: [{ label: 'Vacancy', value: '60%' }, { label: 'Food Desert', value: '88%' }, { label: 'Poverty', value: '50%' }] },
    ],
    topResources: [
      { name: 'American Moslem Society', category: 'Community', nameOfAllah: 'AL-WADUD' },
      { name: 'Brightmoor Farmway', category: 'Food', nameOfAllah: 'AR-RAZZAQ' },
      { name: 'ACCESS — Arab Community Center', category: 'Community', nameOfAllah: 'AL-WADUD' },
    ],
    ayat: { arabic: 'لَا تَقْنَطُوا مِن رَّحْمَةِ اللَّهِ', ref: '39:53', nameOfAllah: 'AL-AFUWW', arabicName: 'الْعَفُوّ', meaning: 'The Pardoner' },
    disparities: [
      { issue: 'Vacancy Rate', value: '55%', ward: 'Brightmoor', nameOfAllah: 'AL-WALI', arabicName: 'الْوَلِيّ' },
      { issue: 'Food Desert', value: '85%', ward: 'East Side', nameOfAllah: 'AR-RAZZAQ', arabicName: 'الرَّزَّاق' },
    ],
    context: 'Hamtramck — first all-Muslim city council. Brightmoor Farmway — food growing where houses stood.',
    openLetterQuote: 'If the Hour is established and one has a palm shoot, let him plant it. Brightmoor Farmway is this Hadith made operational.',
    wardCount: 8,
    wardSeverities: ['HIGH', 'HIGH', 'CRITICAL', 'HIGH', 'MODERATE', 'HIGH', 'HIGH', 'CRITICAL'] as const,
  },
];

const SEVERITY_COLORS: Record<string, string> = {
  CRITICAL: '#E05252',
  HIGH: '#C9A84C',
  MODERATE: '#7EC8A0',
};

type MRGTab = 'NAMES' | 'AYAT' | 'UBOOR' | 'RESOURCES' | 'CONTEXT' | 'DATA';

const TABS_FULL: MRGTab[] = ['NAMES', 'AYAT', 'UBOOR', 'RESOURCES', 'CONTEXT', 'DATA'];
const TABS_COMPACT: MRGTab[] = ['UBOOR', 'RESOURCES', 'CONTEXT'];

// ── UBOOR Panel ───────────────────────────────────────────────────────────────
function UboorPanel({ city, compact }: { city: typeof CITIES_MRG[0]; compact: boolean }) {
  const [step, setStep] = useState(0);
  const UBOOR_STEPS = [
    { label: 'Zahir', arabic: 'الظَّاهِر', desc: 'What is outwardly visible in this ward — the surface reality.', prompt: 'What do you observe in ' + city.name + '?' },
    { label: 'Sunnat Allah', arabic: 'سُنَّة اللَّه', desc: 'The patterns and laws woven into this community.', prompt: 'What systemic pattern is at work here?' },
    { label: 'Haqq', arabic: 'الْحَقّ', desc: 'The deeper truth beneath the disparity.', prompt: 'What is the root cause?' },
    { label: 'Amanah', arabic: 'الأَمَانَة', desc: 'The trust and responsibility this recognition calls you to.', prompt: 'What is your responsibility here?' },
    { label: 'Uboor', arabic: 'عُبُور', desc: 'Crossing — recognition becomes a bridge to action.', prompt: 'How does this cross into action?' },
  ];
  const s = UBOOR_STEPS[step];
  if (compact) {
    return (
      <div style={{ padding: '8px 10px' }}>
        <div style={{ display: 'flex', gap: 3, marginBottom: 6 }}>
          {UBOOR_STEPS.map((_, i) => (
            <button key={i} onClick={() => setStep(i)} style={{ flex: 1, height: 3, background: i === step ? '#2d8a87' : 'rgba(45,138,135,0.2)', border: 'none', cursor: 'pointer', borderRadius: 2 }} />
          ))}
        </div>
        <p style={{ fontFamily: 'monospace', fontSize: 8, color: '#2d8a87', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 2 }}>{s.label}</p>
        <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 9, lineHeight: 1.4 }}>{s.prompt}</p>
      </div>
    );
  }
  return (
    <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', gap: 4 }}>
        {UBOOR_STEPS.map((l, i) => (
          <button key={i} onClick={() => setStep(i)} style={{ flex: 1, padding: '4px 2px', background: i === step ? 'rgba(45,138,135,0.15)' : 'transparent', border: `1px solid ${i === step ? 'rgba(45,138,135,0.5)' : 'rgba(45,138,135,0.12)'}`, borderRadius: 4, color: i === step ? '#2d8a87' : 'rgba(45,138,135,0.4)', fontFamily: 'monospace', fontSize: 7, letterSpacing: '.04em', textTransform: 'uppercase', cursor: 'pointer' }}>
            {l.label.split(' ')[0]}
          </button>
        ))}
      </div>
      <div style={{ background: 'rgba(45,138,135,0.05)', border: '1px solid rgba(45,138,135,0.15)', borderRadius: 6, padding: '10px 12px' }}>
        <p style={{ fontFamily: "'Amiri', serif", fontSize: 14, color: 'rgba(45,138,135,0.7)', direction: 'rtl', marginBottom: 3 }}>{s.arabic}</p>
        <p style={{ fontFamily: 'monospace', fontSize: 10, color: '#F5F0E8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 4 }}>{s.label}</p>
        <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 10, lineHeight: 1.5, marginBottom: 4 }}>{s.desc}</p>
        <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(45,138,135,0.6)', fontStyle: 'italic' }}>"{s.prompt}"</p>
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <button onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0} style={{ flex: 1, padding: '5px', background: 'transparent', border: '1px solid rgba(45,138,135,0.2)', borderRadius: 4, color: 'rgba(45,138,135,0.5)', fontFamily: 'monospace', fontSize: 7, cursor: 'pointer', opacity: step === 0 ? 0.3 : 1 }}>← Prev</button>
        <button onClick={() => setStep(s => Math.min(4, s + 1))} disabled={step === 4} style={{ flex: 1, padding: '5px', background: 'rgba(45,138,135,0.12)', border: '1px solid rgba(45,138,135,0.35)', borderRadius: 4, color: '#2d8a87', fontFamily: 'monospace', fontSize: 7, cursor: 'pointer', opacity: step === 4 ? 0.3 : 1 }}>Next →</button>
      </div>
    </div>
  );
}

// ── Names/Attributes Panel ────────────────────────────────────────────────────
function NamesPanel({ compact }: { compact: boolean }) {
  const [activeIdx, setActiveIdx] = useState(0);
  const cat = RESOURCE_CATEGORIES_MRG[activeIdx];
  if (compact) {
    return (
      <div style={{ padding: '8px 10px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginBottom: 6 }}>
          {RESOURCE_CATEGORIES_MRG.slice(0, 4).map((c, i) => (
            <button key={i} onClick={() => setActiveIdx(i)} style={{ padding: '2px 6px', background: i === activeIdx ? 'rgba(138,138,184,0.2)' : 'transparent', border: `1px solid ${i === activeIdx ? 'rgba(138,138,184,0.5)' : 'rgba(138,138,184,0.15)'}`, borderRadius: 3, color: i === activeIdx ? '#8a8ab8' : 'rgba(138,138,184,0.4)', fontFamily: 'monospace', fontSize: 7, cursor: 'pointer' }}>
              {c.icon}
            </button>
          ))}
        </div>
        <p style={{ fontFamily: 'monospace', fontSize: 8, color: '#8a8ab8', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 1 }}>{cat.nameOfAllah}</p>
        <p style={{ fontFamily: "'Amiri', serif", fontSize: 12, color: 'rgba(138,138,184,0.7)', direction: 'rtl' }}>{cat.arabicName}</p>
        <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 9 }}>{cat.meaning}</p>
      </div>
    );
  }
  return (
    <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        {RESOURCE_CATEGORIES_MRG.map((c, i) => (
          <button key={i} onClick={() => setActiveIdx(i)} style={{ padding: '3px 7px', background: i === activeIdx ? 'rgba(138,138,184,0.15)' : 'transparent', border: `1px solid ${i === activeIdx ? 'rgba(138,138,184,0.4)' : 'rgba(138,138,184,0.12)'}`, borderRadius: 3, color: i === activeIdx ? '#8a8ab8' : 'rgba(138,138,184,0.35)', fontFamily: 'monospace', fontSize: 7, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3 }}>
            <span>{c.icon}</span><span>{c.key}</span>
          </button>
        ))}
      </div>
      <div style={{ background: 'rgba(138,138,184,0.05)', border: '1px solid rgba(138,138,184,0.15)', borderRadius: 6, padding: '10px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 6 }}>
          <div>
            <p style={{ fontFamily: 'monospace', fontSize: 9, color: '#8a8ab8', letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 2 }}>{cat.nameOfAllah}</p>
            <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 9 }}>{cat.meaning}</p>
          </div>
          <p style={{ fontFamily: "'Amiri', serif", fontSize: 18, color: 'rgba(138,138,184,0.6)', direction: 'rtl' }}>{cat.arabicName}</p>
        </div>
        <p style={{ fontFamily: "'Amiri', serif", fontSize: 11, color: 'rgba(138,138,184,0.5)', direction: 'rtl', marginBottom: 4, lineHeight: 1.6 }}>{cat.ayat.arabic}</p>
        <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(138,138,184,0.4)', marginBottom: 6 }}>Quran {cat.ayat.ref} — {cat.ayat.text}</p>
        <p style={{ color: 'rgba(212,197,169,0.45)', fontSize: 9, lineHeight: 1.5 }}>{cat.context}</p>
      </div>
    </div>
  );
}

// ── Ayat Mosaic Panel — with citywide resource list when tapped ───────────────
function AyatMosaicPanel({ city, compact }: { city: typeof CITIES_MRG[0]; compact: boolean }) {
  const [activeAyat, setActiveAyat] = useState(0);
  const [showCitywideResources, setShowCitywideResources] = useState(false);
  const AYAT_TILES = RESOURCE_CATEGORIES_MRG.map(c => ({ ...c.ayat, category: c.key, nameOfAllah: c.nameOfAllah, arabicName: c.arabicName, meaning: c.meaning, icon: c.icon }));
  const tile = AYAT_TILES[activeAyat];

  // Get all resources for the selected ayat's Name of Allah (citywide)
  const citywideResources = city.topResources.filter(r => r.nameOfAllah === tile.nameOfAllah);
  const allResourcesForName = RESOURCE_CATEGORIES_MRG.find(c => c.nameOfAllah === tile.nameOfAllah);

  if (compact) {
    return (
      <div style={{ padding: '8px 10px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2, marginBottom: 6 }}>
          {AYAT_TILES.slice(0, 6).map((t, i) => (
            <button key={i} onClick={() => setActiveAyat(i)} style={{ width: 20, height: 20, background: i === activeAyat ? 'rgba(45,122,71,0.3)' : 'rgba(45,122,71,0.08)', border: `1px solid ${i === activeAyat ? 'rgba(45,122,71,0.5)' : 'rgba(45,122,71,0.15)'}`, borderRadius: 2, fontSize: 9, cursor: 'pointer' }}>{t.icon}</button>
          ))}
        </div>
        <p style={{ fontFamily: "'Amiri', serif", fontSize: 10, color: 'rgba(45,122,71,0.7)', direction: 'rtl', lineHeight: 1.5 }}>{tile.arabic}</p>
        <p style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(45,122,71,0.5)' }}>{tile.ref} · {tile.nameOfAllah}</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/* Mosaic grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 3 }}>
        {AYAT_TILES.map((t, i) => (
          <button key={i} onClick={() => { setActiveAyat(i); setShowCitywideResources(false); }} style={{ padding: '6px 4px', background: i === activeAyat ? 'rgba(45,122,71,0.2)' : 'rgba(45,122,71,0.05)', border: `1px solid ${i === activeAyat ? 'rgba(45,122,71,0.5)' : 'rgba(45,122,71,0.12)'}`, borderRadius: 4, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <span style={{ fontSize: 12 }}>{t.icon}</span>
            <span style={{ fontFamily: 'monospace', fontSize: 6, color: i === activeAyat ? '#2d7a47' : 'rgba(45,122,71,0.4)', textTransform: 'uppercase', letterSpacing: '.04em' }}>{t.category}</span>
          </button>
        ))}
      </div>

      {/* Active ayat — tappable to show citywide resources */}
      <button
        onClick={() => setShowCitywideResources(s => !s)}
        style={{ background: 'rgba(45,122,71,0.05)', border: `1px solid ${showCitywideResources ? 'rgba(45,122,71,0.4)' : 'rgba(45,122,71,0.15)'}`, borderRadius: 6, padding: '10px 12px', cursor: 'pointer', textAlign: 'left', width: '100%' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
          <span style={{ fontFamily: 'monospace', fontSize: 8, color: '#2d7a47', letterSpacing: '.1em', textTransform: 'uppercase', background: 'rgba(45,122,71,0.1)', border: '1px solid rgba(45,122,71,0.25)', padding: '2px 6px', borderRadius: 3 }}>{tile.nameOfAllah} · {tile.arabicName}</span>
          <span style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(45,122,71,0.5)' }}>{tile.ref}</span>
          <span style={{ marginLeft: 'auto', fontFamily: 'monospace', fontSize: 7, color: 'rgba(45,122,71,0.5)' }}>{showCitywideResources ? '▲ Hide' : '▼ Resources'}</span>
        </div>
        <p style={{ fontFamily: "'Amiri', serif", fontSize: 14, color: 'rgba(45,122,71,0.8)', direction: 'rtl', lineHeight: 1.8, marginBottom: 4 }}>{tile.arabic}</p>
        <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 9, lineHeight: 1.5, fontStyle: 'italic' }}>"{tile.text}"</p>
        <p style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(45,122,71,0.4)', marginTop: 4 }}>{tile.meaning}</p>
      </button>

      {/* Citywide resource list — shown when ayat is tapped */}
      {showCitywideResources && (
        <div style={{ background: 'rgba(45,122,71,0.04)', border: '1px solid rgba(45,122,71,0.15)', borderRadius: 6, padding: '10px 12px' }}>
          <p style={{ fontFamily: 'monospace', fontSize: 7, color: '#2d7a47', letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 8 }}>
            ⬡ {city.name} · Citywide Resources · {tile.nameOfAllah}
          </p>

          {/* Name of Allah header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, padding: '6px 8px', background: 'rgba(138,138,184,0.06)', border: '1px solid rgba(138,138,184,0.12)', borderRadius: 4 }}>
            <span style={{ fontFamily: "'Amiri', serif", fontSize: 16, color: 'rgba(138,138,184,0.7)', direction: 'rtl' }}>{tile.arabicName}</span>
            <div>
              <p style={{ fontFamily: 'monospace', fontSize: 8, color: '#8a8ab8', letterSpacing: '.08em', textTransform: 'uppercase' }}>{tile.nameOfAllah}</p>
              <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 8 }}>{tile.meaning}</p>
            </div>
          </div>

          {/* Ayat */}
          <div style={{ padding: '6px 8px', background: 'rgba(45,122,71,0.04)', border: '1px solid rgba(45,122,71,0.1)', borderRadius: 4, marginBottom: 8 }}>
            <p style={{ fontFamily: "'Amiri', serif", fontSize: 12, color: 'rgba(45,122,71,0.7)', direction: 'rtl', lineHeight: 1.7, marginBottom: 2 }}>{tile.arabic}</p>
            <p style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(45,122,71,0.4)' }}>Quran {tile.ref}</p>
          </div>

          {/* Context */}
          {allResourcesForName && (
            <p style={{ color: 'rgba(212,197,169,0.45)', fontSize: 9, lineHeight: 1.5, marginBottom: 8, fontStyle: 'italic' }}>{allResourcesForName.context}</p>
          )}

          {/* Citywide resources for this Name */}
          {citywideResources.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <p style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(45,122,71,0.4)', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 2 }}>Resources in {city.name}</p>
              {citywideResources.map((r, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 7px', background: 'rgba(45,122,71,0.04)', border: '1px solid rgba(45,122,71,0.1)', borderRadius: 4 }}>
                  <span style={{ fontSize: 10 }}>{allResourcesForName?.icon || '⬡'}</span>
                  <div>
                    <p style={{ color: 'rgba(212,197,169,0.75)', fontSize: 9, lineHeight: 1.3 }}>{r.name}</p>
                    <p style={{ fontFamily: 'monospace', fontSize: 6, color: 'rgba(45,122,71,0.4)' }}>{r.category}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ padding: '8px', background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.1)', borderRadius: 4 }}>
              <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(201,168,76,0.4)', textAlign: 'center' }}>
                No resources yet documented for {tile.nameOfAllah} in {city.name}
              </p>
              <p style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(201,168,76,0.25)', textAlign: 'center', marginTop: 3 }}>Submit a resource to grow this collection</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Resources Panel ───────────────────────────────────────────────────────────
function ResourcesPanel({ city, compact }: { city: typeof CITIES_MRG[0]; compact: boolean }) {
  const [filter, setFilter] = useState<string | null>(null);
  const filtered = filter ? city.topResources.filter(r => r.category === filter) : city.topResources;
  if (compact) {
    return (
      <div style={{ padding: '8px 10px' }}>
        {city.topResources.slice(0, 3).map((r, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, paddingBottom: 4, borderBottom: i < 2 ? '1px solid rgba(45,122,71,0.08)' : 'none' }}>
            <span style={{ fontFamily: 'monospace', fontSize: 7, color: '#2d7a47', background: 'rgba(45,122,71,0.1)', padding: '1px 4px', borderRadius: 2, flexShrink: 0 }}>{r.category}</span>
            <span style={{ color: 'rgba(212,197,169,0.7)', fontSize: 9, lineHeight: 1.3 }}>{r.name}</span>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/* Category filter */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        <button onClick={() => setFilter(null)} style={{ padding: '2px 7px', background: !filter ? 'rgba(45,122,71,0.15)' : 'transparent', border: `1px solid ${!filter ? 'rgba(45,122,71,0.4)' : 'rgba(45,122,71,0.12)'}`, borderRadius: 3, color: !filter ? '#2d7a47' : 'rgba(45,122,71,0.4)', fontFamily: 'monospace', fontSize: 7, cursor: 'pointer' }}>All</button>
        {['Community', 'Food', 'Housing', 'Reentry', 'Income'].map(cat => (
          <button key={cat} onClick={() => setFilter(cat)} style={{ padding: '2px 7px', background: filter === cat ? 'rgba(45,122,71,0.15)' : 'transparent', border: `1px solid ${filter === cat ? 'rgba(45,122,71,0.4)' : 'rgba(45,122,71,0.12)'}`, borderRadius: 3, color: filter === cat ? '#2d7a47' : 'rgba(45,122,71,0.4)', fontFamily: 'monospace', fontSize: 7, cursor: 'pointer' }}>{cat}</button>
        ))}
      </div>
      {/* Resources list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {filtered.map((r, i) => {
          const cat = RESOURCE_CATEGORIES_MRG.find(c => c.key === r.category);
          return (
            <div key={i} style={{ background: 'rgba(45,122,71,0.04)', border: '1px solid rgba(45,122,71,0.12)', borderRadius: 5, padding: '7px 9px', display: 'flex', alignItems: 'flex-start', gap: 7 }}>
              <span style={{ fontSize: 12, flexShrink: 0 }}>{cat?.icon || '⬡'}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ color: 'rgba(212,197,169,0.8)', fontSize: 9, lineHeight: 1.3, marginBottom: 2 }}>{r.name}</p>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  <span style={{ fontFamily: 'monospace', fontSize: 6, color: '#2d7a47', background: 'rgba(45,122,71,0.1)', padding: '1px 4px', borderRadius: 2 }}>{r.category}</span>
                  <span style={{ fontFamily: 'monospace', fontSize: 6, color: 'rgba(138,138,184,0.6)', background: 'rgba(138,138,184,0.06)', padding: '1px 4px', borderRadius: 2 }}>{r.nameOfAllah}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {/* Masajid */}
      <div style={{ borderTop: '1px solid rgba(45,122,71,0.1)', paddingTop: 6 }}>
        <p style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(45,122,71,0.5)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 4 }}>Masajid · {city.masajid.length} documented</p>
        {city.masajid.map((m, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
            <span style={{ fontSize: 8 }}>🕌</span>
            <span style={{ color: 'rgba(212,197,169,0.55)', fontSize: 8 }}>{m}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Context Panel ─────────────────────────────────────────────────────────────
function ContextPanel({ city, compact }: { city: typeof CITIES_MRG[0]; compact: boolean }) {
  if (compact) {
    return (
      <div style={{ padding: '8px 10px' }}>
        <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 9, lineHeight: 1.5, marginBottom: 4 }}>{city.context.slice(0, 80)}...</p>
        <p style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(201,168,76,0.5)', fontStyle: 'italic' }}>"{city.openLetterQuote.slice(0, 60)}..."</p>
      </div>
    );
  }
  return (
    <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.12)', borderRadius: 6, padding: '10px 12px' }}>
        <p style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(201,168,76,0.5)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 4 }}>City Context · {city.name}</p>
        <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 10, lineHeight: 1.6 }}>{city.context}</p>
      </div>
      <div style={{ background: 'rgba(201,168,76,0.03)', border: '1px solid rgba(201,168,76,0.1)', borderRadius: 6, padding: '10px 12px' }}>
        <p style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(201,168,76,0.4)', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 4 }}>Open Letter · Foundational Quote</p>
        <p style={{ fontFamily: "'Georgia', serif", fontSize: 10, color: 'rgba(212,197,169,0.6)', lineHeight: 1.7, fontStyle: 'italic', borderLeft: '2px solid rgba(201,168,76,0.25)', paddingLeft: 8 }}>"{city.openLetterQuote}"</p>
      </div>
      {/* Ayat for city */}
      <div style={{ background: 'rgba(45,122,71,0.04)', border: '1px solid rgba(45,122,71,0.12)', borderRadius: 6, padding: '10px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
          <span style={{ fontFamily: 'monospace', fontSize: 7, color: '#2d7a47', background: 'rgba(45,122,71,0.1)', padding: '1px 5px', borderRadius: 2 }}>{city.ayat.nameOfAllah} · {city.ayat.arabicName}</span>
          <span style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(45,122,71,0.4)' }}>Quran {city.ayat.ref}</span>
        </div>
        <p style={{ fontFamily: "'Amiri', serif", fontSize: 12, color: 'rgba(45,122,71,0.7)', direction: 'rtl', lineHeight: 1.7 }}>{city.ayat.arabic}</p>
        <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 8, marginTop: 3, fontStyle: 'italic' }}>{city.ayat.meaning}</p>
      </div>
    </div>
  );
}

// ── Data Panel ────────────────────────────────────────────────────────────────
function DataPanel({ city, compact }: { city: typeof CITIES_MRG[0]; compact: boolean }) {
  if (compact) {
    return (
      <div style={{ padding: '8px 10px' }}>
        {city.wardStats.map((s, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
            <span style={{ color: 'rgba(212,197,169,0.5)', fontSize: 8 }}>{s.label}</span>
            <span style={{ fontFamily: 'monospace', fontSize: 9, color: SEVERITY_COLORS.CRITICAL, fontWeight: 700 }}>{s.value}</span>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/* Ward stats */}
      <div>
        <p style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.3)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 6 }}>Ward Indicators · {city.keyWard}</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4 }}>
          {city.wardStats.map((s, i) => (
            <div key={i} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 4, padding: '6px 8px', textAlign: 'center' }}>
              <p style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: SEVERITY_COLORS.CRITICAL, marginBottom: 2 }}>{s.value}</p>
              <p style={{ fontFamily: 'monospace', fontSize: 6, color: 'rgba(212,197,169,0.35)', textTransform: 'uppercase', letterSpacing: '.04em' }}>{s.label}</p>
            </div>
          ))}
        </div>
      </div>
      {/* Disparities */}
      <div>
        <p style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.3)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 4 }}>Disparities · Islah Calls</p>
        {city.disparities.map((d, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, padding: '5px 7px', background: 'rgba(224,82,82,0.04)', border: '1px solid rgba(224,82,82,0.1)', borderRadius: 4 }}>
            <span style={{ fontFamily: 'monospace', fontSize: 10, fontWeight: 700, color: SEVERITY_COLORS.CRITICAL, flexShrink: 0 }}>{d.value}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 8, marginBottom: 1 }}>{d.issue} · {d.ward}</p>
              <p style={{ fontFamily: 'monospace', fontSize: 6, color: 'rgba(138,138,184,0.5)' }}>{d.nameOfAllah} · {d.arabicName}</p>
            </div>
          </div>
        ))}
      </div>
      {/* Ward tile mosaic */}
      <div>
        <p style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.3)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 4 }}>Ward Severity Mosaic · {city.wardCount} Wards</p>
        <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {city.wardSeverities.map((sev, i) => (
            <div key={i} style={{ width: `${20 + (i % 4) * 6}px`, height: `${14 + (i % 3) * 4}px`, background: SEVERITY_COLORS[sev], opacity: 0.5 + (i % 3) * 0.15, borderRadius: 2 }} title={`Ward ${i + 1}: ${sev}`} />
          ))}
        </div>
        <p style={{ fontFamily: 'monospace', fontSize: 6, color: 'rgba(212,197,169,0.2)', marginTop: 3 }}>
          <span style={{ color: '#E05252' }}>■</span> CRITICAL &nbsp;
          <span style={{ color: '#C9A84C' }}>■</span> HIGH &nbsp;
          <span style={{ color: '#7EC8A0' }}>■</span> MODERATE
        </p>
      </div>
    </div>
  );
}

// ── MAIN MRG WIDGET ───────────────────────────────────────────────────────────
export default function MRGFullWidget({ size, color, city: cityProp }: MRGWidgetProps) {
  const [activeCity, setActiveCity] = useState(cityProp || 'chicago');
  const [activeTab, setActiveTab] = useState<MRGTab>('UBOOR');
  const [activeWard, setActiveWard] = useState<string | null>(null);

  const city = CITIES_MRG.find(c => c.id === activeCity) || CITIES_MRG[0];
  const isCompact = size === 'sm';
  const isMedium = size === 'md';
  const isLarge = size === 'lg' || size === 'xl';
  const tabs = isCompact ? TABS_COMPACT : TABS_FULL;

  const selectedWard = activeWard ? city.wards?.find(w => w.name === activeWard) : null;

  // sm: minimal — just city selector + one panel
  if (isCompact) {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'rgba(6,13,18,0.95)', overflow: 'hidden' }}>
        {/* City selector */}
        <div style={{ display: 'flex', gap: 1, padding: '6px 8px', borderBottom: '1px solid rgba(45,122,71,0.15)', flexShrink: 0 }}>
          {CITIES_MRG.map(c => (
            <button key={c.id} onClick={() => setActiveCity(c.id)} style={{ flex: 1, padding: '3px 2px', background: activeCity === c.id ? 'rgba(45,122,71,0.2)' : 'transparent', border: `1px solid ${activeCity === c.id ? 'rgba(45,122,71,0.4)' : 'rgba(45,122,71,0.1)'}`, borderRadius: 3, color: activeCity === c.id ? '#2d7a47' : 'rgba(45,122,71,0.35)', fontFamily: 'monospace', fontSize: 6, cursor: 'pointer', letterSpacing: '.04em', textTransform: 'uppercase' }}>
              {c.name.slice(0, 3)}
            </button>
          ))}
        </div>
        {/* Tab selector */}
        <div style={{ display: 'flex', gap: 1, padding: '4px 8px', borderBottom: '1px solid rgba(45,122,71,0.1)', flexShrink: 0 }}>
          {tabs.map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)} style={{ flex: 1, padding: '2px', background: activeTab === tab ? 'rgba(45,122,71,0.15)' : 'transparent', border: `1px solid ${activeTab === tab ? 'rgba(45,122,71,0.35)' : 'transparent'}`, borderRadius: 2, color: activeTab === tab ? '#2d7a47' : 'rgba(45,122,71,0.3)', fontFamily: 'monospace', fontSize: 5, cursor: 'pointer', letterSpacing: '.04em', textTransform: 'uppercase' }}>
              {tab.slice(0, 3)}
            </button>
          ))}
        </div>
        {/* Panel content */}
        <div style={{ flex: 1, overflow: 'hidden' }}>
          {activeTab === 'UBOOR' && <UboorPanel city={city} compact />}
          {activeTab === 'RESOURCES' && <ResourcesPanel city={city} compact />}
          {activeTab === 'CONTEXT' && <ContextPanel city={city} compact />}
          {activeTab === 'NAMES' && <NamesPanel compact />}
          {activeTab === 'AYAT' && <AyatMosaicPanel city={city} compact />}
          {activeTab === 'DATA' && <DataPanel city={city} compact />}
        </div>
        {/* Footer link */}
        <div style={{ padding: '4px 8px', borderTop: '1px solid rgba(45,122,71,0.1)', flexShrink: 0 }}>
          <Link href="/muslim-resource-guide" style={{ fontFamily: 'monospace', fontSize: 6, color: 'rgba(45,122,71,0.5)', textDecoration: 'none', letterSpacing: '.06em' }}>Full MRG →</Link>
        </div>
      </div>
    );
  }

  // lg/xl (4x2): Full MRG page mirror — city dropdown, all wards, full tabs
  if (isLarge) {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'rgba(6,13,18,0.97)', overflow: 'hidden' }}>
        {/* Header with city dropdown */}
        <div style={{ padding: '10px 14px', borderBottom: '1px solid rgba(45,122,71,0.15)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <div>
              <p style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(45,122,71,0.5)', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 1 }}>Muslim Resource Guide · دليل الموارد</p>
              <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.3)', letterSpacing: '.06em' }}>Haqq · Amanah · Islah — fisabilillah</p>
            </div>
            <Link href="/muslim-resource-guide" style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(45,122,71,0.5)', textDecoration: 'none', border: '1px solid rgba(45,122,71,0.2)', padding: '2px 6px', borderRadius: 3 }}>Full MRG ↗</Link>
          </div>
          {/* City dropdown selector */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <p style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(45,122,71,0.4)', letterSpacing: '.1em', textTransform: 'uppercase', flexShrink: 0 }}>City:</p>
            <select
              value={activeCity}
              onChange={e => { setActiveCity(e.target.value); setActiveWard(null); }}
              style={{ flex: 1, padding: '4px 8px', background: 'rgba(45,122,71,0.08)', border: '1px solid rgba(45,122,71,0.25)', borderRadius: 4, color: '#2d7a47', fontFamily: 'monospace', fontSize: 9, cursor: 'pointer', outline: 'none' }}
            >
              {CITIES_MRG.map(c => (
                <option key={c.id} value={c.id} style={{ background: '#060d12' }}>{c.name} — {c.arabicName}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Two-column layout: wards list + main content */}
        <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* Left: All wards for selected city */}
          <div style={{ width: 140, borderRight: '1px solid rgba(45,122,71,0.12)', display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0 }}>
            <div style={{ padding: '6px 8px', borderBottom: '1px solid rgba(45,122,71,0.1)', flexShrink: 0 }}>
              <p style={{ fontFamily: 'monospace', fontSize: 6, color: 'rgba(45,122,71,0.4)', letterSpacing: '.1em', textTransform: 'uppercase' }}>Wards · {city.name}</p>
            </div>
            <div style={{ flex: 1, overflow: 'auto' }}>
              {/* All Wards option */}
              <button
                onClick={() => setActiveWard(null)}
                style={{ width: '100%', padding: '6px 8px', background: !activeWard ? 'rgba(45,122,71,0.12)' : 'transparent', border: 'none', borderBottom: '1px solid rgba(45,122,71,0.06)', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              >
                <span style={{ fontFamily: 'monospace', fontSize: 7, color: !activeWard ? '#2d7a47' : 'rgba(45,122,71,0.4)', letterSpacing: '.04em' }}>All Wards</span>
                <span style={{ fontFamily: 'monospace', fontSize: 6, color: 'rgba(45,122,71,0.3)' }}>{city.wardCount}</span>
              </button>
              {/* Individual wards */}
              {city.wards?.map((ward, i) => (
                <button
                  key={i}
                  onClick={() => setActiveWard(ward.name === activeWard ? null : ward.name)}
                  style={{ width: '100%', padding: '6px 8px', background: activeWard === ward.name ? 'rgba(45,122,71,0.12)' : 'transparent', border: 'none', borderBottom: '1px solid rgba(45,122,71,0.05)', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                >
                  <span style={{ fontFamily: 'monospace', fontSize: 7, color: activeWard === ward.name ? '#2d7a47' : 'rgba(212,197,169,0.4)', letterSpacing: '.03em', textAlign: 'left' }}>{ward.name}</span>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: SEVERITY_COLORS[ward.severity], flexShrink: 0 }} title={ward.severity} />
                </button>
              ))}
            </div>
            {/* Ward severity legend */}
            <div style={{ padding: '6px 8px', borderTop: '1px solid rgba(45,122,71,0.1)', flexShrink: 0 }}>
              {['CRITICAL', 'HIGH', 'MODERATE'].map(s => (
                <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
                  <span style={{ width: 5, height: 5, borderRadius: '50%', background: SEVERITY_COLORS[s] }} />
                  <span style={{ fontFamily: 'monospace', fontSize: 5, color: 'rgba(212,197,169,0.25)', textTransform: 'uppercase', letterSpacing: '.04em' }}>{s}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Main content */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Ward info bar */}
            <div style={{ padding: '5px 12px', borderBottom: '1px solid rgba(45,122,71,0.1)', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              {selectedWard ? (
                <>
                  <span style={{ fontFamily: 'monospace', fontSize: 8, color: '#F5F0E8' }}>{selectedWard.name}</span>
                  <span style={{ fontFamily: 'monospace', fontSize: 6, color: SEVERITY_COLORS[selectedWard.severity], background: `${SEVERITY_COLORS[selectedWard.severity]}15`, border: `1px solid ${SEVERITY_COLORS[selectedWard.severity]}30`, padding: '1px 5px', borderRadius: 2, letterSpacing: '.06em' }}>{selectedWard.severity}</span>
                  {selectedWard.stats.map((s, i) => (
                    <span key={i} style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.35)' }}>{s.label}: <span style={{ color: SEVERITY_COLORS.CRITICAL }}>{s.value}</span></span>
                  ))}
                </>
              ) : (
                <>
                  <span style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.4)', letterSpacing: '.08em', textTransform: 'uppercase' }}>All Wards · {city.name}</span>
                  <span style={{ fontFamily: 'monospace', fontSize: 8, color: '#F5F0E8' }}>{city.keyWard}</span>
                  <span style={{ fontFamily: 'monospace', fontSize: 6, color: SEVERITY_COLORS[city.wardSeverity], background: `${SEVERITY_COLORS[city.wardSeverity]}15`, border: `1px solid ${SEVERITY_COLORS[city.wardSeverity]}30`, padding: '1px 5px', borderRadius: 2, letterSpacing: '.06em' }}>{city.wardSeverity}</span>
                </>
              )}
            </div>

            {/* Panel tabs */}
            <div style={{ display: 'flex', borderBottom: '1px solid rgba(45,122,71,0.12)', flexShrink: 0 }}>
              {TABS_FULL.map(tab => (
                <button key={tab} onClick={() => setActiveTab(tab)} style={{ flex: 1, padding: '6px 4px', background: activeTab === tab ? 'rgba(45,122,71,0.12)' : 'transparent', border: 'none', borderBottom: `2px solid ${activeTab === tab ? '#2d7a47' : 'transparent'}`, color: activeTab === tab ? '#2d7a47' : 'rgba(45,122,71,0.35)', fontFamily: 'monospace', fontSize: 7, letterSpacing: '.06em', textTransform: 'uppercase', cursor: 'pointer', transition: 'all 0.15s' }}>
                  {tab}
                </button>
              ))}
            </div>

            {/* Panel content */}
            <div style={{ flex: 1, overflow: 'auto' }}>
              {activeTab === 'NAMES' && <NamesPanel compact={false} />}
              {activeTab === 'AYAT' && <AyatMosaicPanel city={city} compact={false} />}
              {activeTab === 'UBOOR' && <UboorPanel city={city} compact={false} />}
              {activeTab === 'RESOURCES' && <ResourcesPanel city={city} compact={false} />}
              {activeTab === 'CONTEXT' && <ContextPanel city={city} compact={false} />}
              {activeTab === 'DATA' && <DataPanel city={city} compact={false} />}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // md: city selector + tabs + panel (no names tab at md)
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'rgba(6,13,18,0.97)', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: isMedium ? '8px 12px' : '10px 14px', borderBottom: '1px solid rgba(45,122,71,0.15)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <div>
            <p style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(45,122,71,0.5)', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 1 }}>Muslim Resource Guide · دليل الموارد</p>
            {!isMedium && <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.3)', letterSpacing: '.06em' }}>Haqq · Amanah · Islah — fisabilillah</p>}
          </div>
          <Link href="/muslim-resource-guide" style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(45,122,71,0.5)', textDecoration: 'none', border: '1px solid rgba(45,122,71,0.2)', padding: '2px 6px', borderRadius: 3 }}>Full MRG ↗</Link>
        </div>
        {/* City tabs */}
        <div style={{ display: 'flex', gap: 1 }}>
          {CITIES_MRG.map(c => (
            <button key={c.id} onClick={() => setActiveCity(c.id)} style={{ flex: 1, padding: isMedium ? '4px 4px' : '5px 6px', background: activeCity === c.id ? 'rgba(45,122,71,0.2)' : 'transparent', border: `1px solid ${activeCity === c.id ? 'rgba(45,122,71,0.45)' : 'rgba(45,122,71,0.12)'}`, borderRadius: 3, cursor: 'pointer', textAlign: 'center' }}>
              <p style={{ fontFamily: 'monospace', fontSize: isMedium ? 7 : 8, color: activeCity === c.id ? '#2d7a47' : 'rgba(45,122,71,0.4)', letterSpacing: '.04em', textTransform: 'uppercase' }}>{c.name}</p>
              {!isMedium && <p style={{ fontFamily: "'Amiri', serif", fontSize: 8, color: activeCity === c.id ? 'rgba(45,122,71,0.6)' : 'rgba(45,122,71,0.2)', direction: 'rtl' }}>{c.arabicName}</p>}
            </button>
          ))}
        </div>
      </div>

      {/* Ward severity badge */}
      <div style={{ padding: isMedium ? '5px 12px' : '6px 14px', borderBottom: '1px solid rgba(45,122,71,0.1)', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        <span style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.4)', letterSpacing: '.08em', textTransform: 'uppercase' }}>Key Ward:</span>
        <span style={{ fontFamily: 'monospace', fontSize: 8, color: '#F5F0E8' }}>{city.keyWard}</span>
        <span style={{ fontFamily: 'monospace', fontSize: 6, color: SEVERITY_COLORS[city.wardSeverity], background: `${SEVERITY_COLORS[city.wardSeverity]}15`, border: `1px solid ${SEVERITY_COLORS[city.wardSeverity]}30`, padding: '1px 5px', borderRadius: 2, letterSpacing: '.06em' }}>{city.wardSeverity}</span>
      </div>

      {/* Panel tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid rgba(45,122,71,0.12)', flexShrink: 0 }}>
        {TABS_FULL.map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{ flex: 1, padding: isMedium ? '5px 2px' : '6px 4px', background: activeTab === tab ? 'rgba(45,122,71,0.12)' : 'transparent', border: 'none', borderBottom: `2px solid ${activeTab === tab ? '#2d7a47' : 'transparent'}`, color: activeTab === tab ? '#2d7a47' : 'rgba(45,122,71,0.35)', fontFamily: 'monospace', fontSize: isMedium ? 6 : 7, letterSpacing: '.06em', textTransform: 'uppercase', cursor: 'pointer', transition: 'all 0.15s' }}>
            {tab}
          </button>
        ))}
      </div>

      {/* Panel content */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {activeTab === 'NAMES' && <NamesPanel compact={false} />}
        {activeTab === 'AYAT' && <AyatMosaicPanel city={city} compact={false} />}
        {activeTab === 'UBOOR' && <UboorPanel city={city} compact={false} />}
        {activeTab === 'RESOURCES' && <ResourcesPanel city={city} compact={false} />}
        {activeTab === 'CONTEXT' && <ContextPanel city={city} compact={false} />}
        {activeTab === 'DATA' && <DataPanel city={city} compact={false} />}
      </div>
    </div>
  );
}
